import { useState, useEffect } from 'react';
import { fdApi } from '../services/api';
import UploadFDModal from './UploadFDModal';
import GenerateFDAnalysisModal from './GenerateFDAnalysisModal';
import GenerateFDV2Modal from './GenerateFDV2Modal';
import '../styles/Panel.css';

function FDManagementPanel({ entity, onComplete }) {
  const [outputs, setOutputs] = useState([]);
  const [outputsV2, setOutputsV2] = useState([]);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showV2Modal, setShowV2Modal] = useState(false);

  useEffect(() => {
    if (entity) {
      fetchOutputs();
      fetchV2Outputs();
    }
  }, [entity]);

  const fetchOutputs = async () => {
    try {
      const data = await fdApi.listFDOutputs(entity.name);
      setOutputs(data.outputs || []);
    } catch (err) {
      console.error('Failed to fetch FD outputs:', err);
    }
  };

  const fetchV2Outputs = async () => {
    try {
      const data = await fdApi.listFDV2Outputs(entity.name);
      setOutputsV2(data.outputs || []);
    } catch (err) {
      console.error('Failed to fetch FD V2 outputs:', err);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setError(null);
      const blob = await fdApi.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'fd_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download template: ' + err.message);
    }
  };

  const handleDownloadFDData = async () => {
    try {
      setError(null);
      const blob = await fdApi.downloadFDData(entity.name);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fd_input_${entity.name}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download FD data: ' + err.message);
    }
  };

  const handleDownloadOutput = async (output) => {
    try {
      setError(null);
      const blob = await fdApi.downloadFDOutput(
        entity.name,
        output.reporting_date,
        output.timestamp
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fd_analysis_${entity.name}_${output.reporting_date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download output: ' + err.message);
    }
  };

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    fetchOutputs();
    if (onComplete) onComplete();
  };

  const handleAnalysisComplete = () => {
    setShowAnalysisModal(false);
    fetchOutputs();
    if (onComplete) onComplete();
  };

  const handleV2Complete = () => {
    setShowV2Modal(false);
    fetchV2Outputs();
    if (onComplete) onComplete();
  };

  const handleDownloadV2Output = async (output) => {
    try {
      setError(null);
      const blob = await fdApi.downloadFDV2Output(
        entity.name,
        output.from_date,
        output.to_date,
        output.timestamp
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fd_v2_schedule_${entity.name}_${output.from_date}_to_${output.to_date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download V2 output: ' + err.message);
    }
  };

  return (
    <div className="panel fd-panel">
      <h2>Fixed Deposit Working (Current/Non-current)</h2>

      {error && <div className="alert alert-error">{error}</div>}

      {/* V1 Action Buttons */}
      <div className="section">
        <h3>V1: Template-Based FD Management</h3>
        <div className="button-group">
          <button
            className="btn btn-secondary"
            onClick={handleDownloadTemplate}
          >
            Download Template
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowUploadModal(true)}
          >
            Upload FD Data
          </button>
        </div>
      </div>

      {/* V2 Action Button */}
      <div className="section" style={{ backgroundColor: '#f0f8ff', padding: '15px', borderRadius: '8px', marginTop: '20px' }}>
        <h3 style={{ color: '#0066cc' }}>V2: AI-Powered PDF Extraction (New!)</h3>
        <p style={{ marginBottom: '10px', fontSize: '0.95em' }}>
          Upload multiple bank PDF statements and let AI automatically extract FD data, classify per Ind AS Schedule 3, and generate comprehensive schedules.
        </p>
        <button
          className="btn btn-primary"
          onClick={() => setShowV2Modal(true)}
          style={{ backgroundColor: '#0066cc' }}
        >
          Generate FD V2 Schedule (AI-Powered)
        </button>
      </div>

      {/* FD Data Section */}
      <div className="section">
        <div className="button-group">
          <button
            className="btn btn-secondary"
            onClick={handleDownloadFDData}
          >
            Download Latest Data
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowAnalysisModal(true)}
          >
            Generate Analysis
          </button>
        </div>
      </div>

      {/* V2 Outputs Section */}
      {outputsV2.length > 0 && (
        <div className="section">
          <h3>AI-Generated FD Schedules (V2)</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Total FDs</th>
                <th>Total Principal</th>
                <th>Total Lien</th>
                <th>Generated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {outputsV2.map((output, index) => (
                <tr key={index}>
                  <td>{output.from_date} to {output.to_date}</td>
                  <td>
                    {output.metadata?.total_fds || '-'}
                  </td>
                  <td>
                    {output.metadata?.total_principal
                      ? `₹ ${parseFloat(output.metadata.total_principal).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                      : '-'}
                  </td>
                  <td>
                    {output.metadata?.total_lien
                      ? `₹ ${parseFloat(output.metadata.total_lien).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                      : '-'}
                  </td>
                  <td>
                    {new Date(output.last_modified).toLocaleString()}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleDownloadV2Output(output)}
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* V1 Analysis Outputs Section */}
      {outputs.length > 0 && (
        <div className="section">
          <h3>Previous Analyses (V1)</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Reporting Date</th>
                <th>Total FDs</th>
                <th>Total Value</th>
                <th>Current</th>
                <th>Non-Current</th>
                <th>Generated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {outputs.map((output, index) => (
                <tr key={index}>
                  <td>{output.reporting_date}</td>
                  <td>
                    {output.metadata?.total_fds || '-'}
                  </td>
                  <td>
                    {output.metadata?.total_principal && output.metadata?.total_accrued_interest
                      ? `₹ ${(parseFloat(output.metadata.total_principal) + parseFloat(output.metadata.total_accrued_interest)).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                      : '-'}
                  </td>
                  <td>
                    {output.metadata?.total_current
                      ? `₹ ${parseFloat(output.metadata.total_current).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                      : '-'}
                  </td>
                  <td>
                    {output.metadata?.total_non_current
                      ? `₹ ${parseFloat(output.metadata.total_non_current).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                      : '-'}
                  </td>
                  <td>
                    {new Date(output.last_modified).toLocaleString()}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleDownloadOutput(output)}
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showUploadModal && (
        <UploadFDModal
          entity={entity}
          onClose={() => setShowUploadModal(false)}
          onComplete={handleUploadComplete}
        />
      )}

      {showAnalysisModal && (
        <GenerateFDAnalysisModal
          entity={entity}
          onClose={() => setShowAnalysisModal(false)}
          onComplete={handleAnalysisComplete}
        />
      )}

      {showV2Modal && (
        <GenerateFDV2Modal
          entity={entity}
          onClose={() => setShowV2Modal(false)}
          onComplete={handleV2Complete}
        />
      )}
    </div>
  );
}

export default FDManagementPanel;
